import { createHOC } from 'vue-hoc';
import gql from 'graphql-tag';
import withUser from '~lib/common/hoc/withUser';

import auth from '~lib/common/providers/auth';
import userManagement from '~lib/common/providers/userManagement';
import casesService from '~lib/common/providers/casesService';

import UserCaseSelector from './UserCaseSelector';
import parseGroupMetadata from '../../../utils/parseGroupMetadata';

const options = {
  name: 'UserCaseSelectorContainer',
  props: {
    user: {
      type: Object,
      required: true,
    },
    loading: {
      type: Boolean,
    }
  },
  data() {
    return {
      state: {
        loading: false,
        copying: false,
        fromEnv: 'dev',
        toEnv: 'dev',
        fromOrgs: [],
        toOrgs: [],
        fromCases: [],
        fromUsers: [],
        toUsers: [],
        error: null,
      },
    };
  },
  async created() {
    if (!(await auth.isAuthenticated())) {
      this.$router.replace({ name: 'login' });
      return;
    }
    this.updateFromOrgs();
  },
  methods: {
    async updateFromOrgs() {
      this.state.fromOrgs = await this.getOrgsFromEnv(this.state.fromEnv);
    },
    async getOrgsFromEnv(env) {
      try {
        this.state.loading = true;
        let orgs = (await userManagement.get(
          '/extension/groups'
        )).data.groups.map((group) => ({
          ...group,
          metadata: parseGroupMetadata(group),
        })).filter(group => !group.metadata.parent);
        return orgs;
      } catch (e) {
        this.state.error = e;
      } finally {
        this.state.loading = false;
      }
    },
  },
};

const renderWith = {
  props: {
    loading() {
      return this.loading || this.state.loading;
    },
    copying() {
      return this.state.copying;
    },
    fromEnv() {
      return this.state.fromEnv;
    },
    toEnv() {
      return this.state.toEnv;
    },
    fromOrgs() {
      return this.state.fromOrgs;
    },
    toOrgs() {
      return this.state.toOrgs;
    },
    fromCases() {
      return this.state.fromCases;
    },
    fromUsers() {
      return this.state.fromUsers;
    },
    toUsers() {
      return this.state.toUsers;
    },
    error() {
      return this.state.error;
    },
  },
  listeners: {
    updateFromEnv(env) {
      this.state.fromEnv = env;
    },
    async updateFromCases(selectedOrg, selectedUser) {
      if (!selectedOrg) {
        return;
      }
      if (!selectedUser) {
        return;
      }
      try {
        this.state.loading = true;
        this.state.error = null;
        const { fromEnv } = this.state;
        const loginAsResponse = await userManagement.post('/auth/loginAs', {
          orgId: selectedOrg._id,
          email: selectedUser.email,
        });
        const authResult = loginAsResponse.data;
        const response = await casesService.query({
          query: gql`
            query getCaseConfigurations {
              caseConfigurations(sortBy: "name") {
                id
                name
              }
            }
          `,
          fetchPolicy: 'network-only',
          context: {
            uri: `https://${fromEnv}.vrpatients.com/graphql`,
            headers: {
              Authorization: `Bearer ${
                authResult.idToken
              }`
            }
          }
        });
        this.state.fromCases = response.data.caseConfigurations;
      } catch (e) {
        console.error(e);
        if (e.graphqlErrors && e.graphqlErrors.length) {
          this.state.error = e.graphqlErrors[0];
        }
        this.state.error = e;
      } finally {
        this.state.loading = false;
      }
    },
    async updateFromUsers(org) {
      try {
        this.state.error = null;
        this.state.loading = true;
        const users = await userManagement.getOrgMembers(org._id);
        this.state.fromUsers = users;
      } catch (e) {
        console.error(e);
        this.state.error = e;
      } finally {
        this.state.loading = false;
      }
    },
  },
};

const UserCaseSelectorContainer = createHOC(UserCaseSelector, options, renderWith);
export default withUser()(UserCaseSelectorContainer);
